import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

export interface FilterOption {
  value: string;
  label: string;
}

export interface MultiSelectFilterProps {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Overrides the aria-label on the search input (default: "Search {label}"). */
  searchAriaLabel?: string;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  searchable = true,
  searchPlaceholder,
  searchAriaLabel,
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, search, searchable]);

  const allSelected = selected.size === options.length && options.length > 0;
  const someSelected = selected.size > 0 && !allSelected;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const toggleAll = () => {
    onChange(allSelected ? new Set() : new Set(options.map((o) => o.value)));
  };

  const placeholder = searchPlaceholder ?? `Search ${label}`;
  const ariaLabelSearch = searchAriaLabel ?? `Search ${label}`;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label} ({selected.size}/{options.length})
        <ChevronDown className="ml-1 h-4 w-4" aria-hidden="true" />
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={`Filter ${label}`}
          className="absolute left-0 z-40 mt-1 w-72 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-3 shadow-lg"
        >
          {searchable && (
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              placeholder={placeholder}
              aria-label={ariaLabelSearch}
            />
          )}
          <div className={`${searchable ? 'mt-3' : 'mt-0'} flex items-center border-b border-[var(--border-hairline)] pb-2`}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleAll}
              label={<span className="text-sm font-medium">Select all</span>}
            />
          </div>
          <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-1 py-2 text-sm text-muted">No matches.</p>
            ) : (
              filtered.map((o) => (
                <div key={o.value} className="flex items-center">
                  <Checkbox
                    checked={selected.has(o.value)}
                    onChange={() => toggle(o.value)}
                    label={
                      <span className="truncate text-sm" title={o.label}>
                        {o.label}
                      </span>
                    }
                  />
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiSelectFilter;
